<script setup>
/* eslint-disable no-use-before-define */
import { computed, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import TalkviaCreditEnginesAPI from 'dashboard/api/talkviaCreditEngines';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const copy = {
  title: 'Motor de crédito bancário',
  description:
    'Cadastre regras por banco e acompanhe o roteiro operacional que será usado na consulta e no funil visual.',
  newEngine: 'Novo motor',
  save: 'Salvar motor',
  update: 'Atualizar motor',
  cancel: 'Cancelar edição',
  loading: 'Carregando motores...',
  empty: 'Nenhum motor cadastrado ainda.',
  failedLoad: 'Não foi possível carregar os motores de crédito.',
  saved: 'Motor de crédito salvo.',
  removed: 'Motor removido.',
  failedSave: 'Não foi possível salvar o motor.',
  failedRemove: 'Não foi possível remover o motor.',
  invalidForm: 'Revise a configuração do motor:',
};

const ufOptions = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

const productTypes = [
  'Consignado INSS',
  'Consignado público',
  'FGTS',
  'Crédito pessoal',
  'Cartão benefício',
  'Portabilidade',
  'Refinanciamento',
];

const productTypeOptions = productTypes.map(t => ({ value: t, label: t }));

const stageColors = ['cyan', 'indigo', 'violet', 'amber', 'emerald', 'rose'];

const checkboxClass =
  'h-4 w-4 rounded border-n-weak bg-n-solid-2 text-n-brand focus:ring-n-brand';

const engines = ref([]);
const selectedEngineId = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const bankFilter = ref('');
const bankOptions = ref([]);
const isLoadingBanks = ref(false);
const showBankOptions = ref(false);

const defaultRules = () => ({
  min_age: 18,
  max_age: 75,
  min_margin: 0,
  max_installment: '',
  min_income: '',
  allowed_ufs: [],
  blocked_ufs: [],
  allowed_benefit_types: [],
  allowed_agreements: [],
  max_term: '',
  requires_authorization: false,
  notes: '',
});

const defaultSteps = () => [
  {
    id: 'triagem',
    title: 'Triagem',
    owner: 'Atendimento',
    sla_minutes: 10,
    description: 'Validar idade, UF, margem e convênio.',
    checklist: ['Documento do cliente', 'Margem disponível'],
  },
  {
    id: 'consulta',
    title: 'Consulta bancária',
    owner: 'Operação',
    sla_minutes: 15,
    description: 'Consultar banco e interpretar retorno.',
    checklist: ['CPF validado', 'Produto selecionado'],
  },
  {
    id: 'proposta',
    title: 'Digitação da proposta',
    owner: 'Formalização',
    sla_minutes: 20,
    description: 'Digitar proposta com a melhor oferta.',
    checklist: ['Tabela confirmada', 'Prazo aprovado'],
  },
  {
    id: 'formalizacao',
    title: 'Formalização',
    owner: 'Cliente',
    sla_minutes: 30,
    description: 'Enviar contrato, selfie e aceite.',
    checklist: ['Contrato assinado', 'Selfie aprovada'],
  },
  {
    id: 'pagamento',
    title: 'Pagamento',
    owner: 'Backoffice',
    sla_minutes: 120,
    description: 'Acompanhar averbação e pagamento.',
    checklist: ['Averbação', 'Pagamento confirmado'],
  },
];

const defaultStages = () => [
  { id: 'coletado', title: 'Dados coletados', color: 'cyan' },
  { id: 'elegivel', title: 'Elegível', color: 'indigo' },
  { id: 'consultado', title: 'Banco consultado', color: 'violet' },
  { id: 'digitado', title: 'Proposta digitada', color: 'amber' },
  { id: 'pago', title: 'Pago', color: 'emerald' },
];

const createBlankForm = () => ({
  bank_name: '',
  name: '',
  code: '',
  product_type: productTypes[0],
  agreement_type: '',
  active: true,
  priority: 0,
  eligibility_rules: defaultRules(),
  operational_steps: defaultSteps(),
  funnel_stages: defaultStages(),
  metadata: {
    table_name: '',
    required_documents: '',
    consultation_provider: '',
    bank_code: '',
  },
});

const form = reactive(createBlankForm());

const selectedEngine = computed(() =>
  engines.value.find(engine => engine.id === selectedEngineId.value)
);

const filteredEngines = computed(() => {
  const query = bankFilter.value.trim().toLowerCase();
  if (!query) return engines.value;
  return engines.value.filter(engine =>
    [engine.bank_name, engine.name, engine.product_type]
      .join(' ')
      .toLowerCase()
      .includes(query)
  );
});

const groupedByBank = computed(() =>
  filteredEngines.value.reduce((groups, engine) => {
    const bank = engine.bank_name || 'Banco sem nome';
    groups[bank] ||= [];
    groups[bank].push(engine);
    return groups;
  }, {})
);

const activeCount = computed(
  () => engines.value.filter(engine => engine.active).length
);

const totalSla = computed(() =>
  form.operational_steps.reduce(
    (sum, step) => sum + Number(step.sla_minutes || 0),
    0
  )
);

const formValidationMessages = computed(() => {
  const messages = [];
  if (!form.bank_name.trim()) messages.push('informe o banco');
  if (!form.name.trim()) messages.push('informe o nome do motor');

  const minAge = numberOrNull(form.eligibility_rules.min_age);
  const maxAge = numberOrNull(form.eligibility_rules.max_age);
  if (minAge !== null && maxAge !== null && minAge > maxAge) {
    messages.push('a idade mínima não pode ser maior que a máxima');
  }

  const conflictingUfs = form.eligibility_rules.allowed_ufs.filter(uf =>
    form.eligibility_rules.blocked_ufs.includes(uf)
  );
  if (conflictingUfs.length) {
    messages.push(
      `remova UFs permitidas e bloqueadas ao mesmo tempo: ${conflictingUfs.join(', ')}`
    );
  }

  [
    ['etapas', form.operational_steps],
    ['fases', form.funnel_stages],
  ].forEach(([label, items]) => {
    if (!items.length) messages.push(`adicione ao menos uma das ${label}`);
    if (items.some(item => !item.id || !item.title?.trim())) {
      messages.push(`preencha identificador e título de todas as ${label}`);
    }
    const ids = items.map(item => item.id).filter(Boolean);
    if (new Set(ids).size !== ids.length) {
      messages.push(`não repita identificadores nas ${label}`);
    }
  });

  return messages;
});

const formIsValid = computed(() => !formValidationMessages.value.length);

const assignForm = engine => {
  Object.assign(form, createBlankForm(), {
    ...engine,
    eligibility_rules: {
      ...defaultRules(),
      ...(engine.eligibility_rules || {}),
    },
    operational_steps: normalizeSteps(engine.operational_steps),
    funnel_stages: normalizeStages(engine.funnel_stages),
    metadata: { ...createBlankForm().metadata, ...(engine.metadata || {}) },
  });
  selectedEngineId.value = engine.id;
};

const resetForm = () => {
  Object.assign(form, createBlankForm());
  selectedEngineId.value = null;
};

const normalizeSteps = steps => {
  if (!Array.isArray(steps) || steps.length === 0) return defaultSteps();
  return steps.map((step, index) => ({
    id: step.id || `etapa-${index + 1}`,
    title: step.title || `Etapa ${index + 1}`,
    owner: step.owner || '',
    sla_minutes: Number(step.sla_minutes || 0),
    description: step.description || '',
    checklist: Array.isArray(step.checklist) ? step.checklist : [],
  }));
};

const normalizeStages = stages => {
  if (!Array.isArray(stages) || stages.length === 0) return defaultStages();
  return stages.map((stage, index) => ({
    id: stage.id || `fase-${index + 1}`,
    title: stage.title || `Fase ${index + 1}`,
    color: stage.color || stageColors[index % stageColors.length],
    description: stage.description || '',
  }));
};

const loadEngines = async () => {
  isLoading.value = true;
  try {
    const { data } = await TalkviaCreditEnginesAPI.get();
    engines.value = data.engines || [];
    if (!selectedEngineId.value && engines.value.length) {
      assignForm(engines.value[0]);
    }
  } catch {
    useAlert(copy.failedLoad);
  } finally {
    isLoading.value = false;
  }
};

const searchBanks = async () => {
  const query = form.bank_name.trim();
  if (query.length < 2) {
    bankOptions.value = [];
    showBankOptions.value = false;
    return;
  }

  isLoadingBanks.value = true;
  try {
    const { data } = await TalkviaCreditEnginesAPI.banks({ q: query });
    bankOptions.value = data.banks || [];
    showBankOptions.value = bankOptions.value.length > 0;
  } catch {
    bankOptions.value = [];
    showBankOptions.value = false;
  } finally {
    isLoadingBanks.value = false;
  }
};

const selectBank = bank => {
  form.bank_name = bank.name;
  form.metadata.bank_code = bank.code;
  bankOptions.value = [];
  showBankOptions.value = false;
};

const payload = () => ({
  bank_name: form.bank_name,
  name: form.name,
  code: form.code,
  product_type: form.product_type,
  agreement_type: form.agreement_type,
  active: form.active,
  priority: Number(form.priority || 0),
  eligibility_rules: {
    ...form.eligibility_rules,
    min_age: numberOrNull(form.eligibility_rules.min_age),
    max_age: numberOrNull(form.eligibility_rules.max_age),
    min_margin: numberOrNull(form.eligibility_rules.min_margin),
    max_installment: numberOrNull(form.eligibility_rules.max_installment),
    min_income: numberOrNull(form.eligibility_rules.min_income),
    max_term: numberOrNull(form.eligibility_rules.max_term),
    allowed_benefit_types: splitList(
      form.eligibility_rules.allowed_benefit_types
    ),
    allowed_agreements: splitList(form.eligibility_rules.allowed_agreements),
  },
  operational_steps: form.operational_steps.map(step => ({
    ...step,
    sla_minutes: Number(step.sla_minutes || 0),
    checklist: splitList(step.checklist),
  })),
  funnel_stages: form.funnel_stages,
  metadata: {
    ...form.metadata,
    bank_code: form.metadata.bank_code || '',
  },
});

const saveEngine = async () => {
  if (!formIsValid.value) {
    useAlert(`${copy.invalidForm} ${formValidationMessages.value.join('; ')}.`);
    return;
  }

  isSaving.value = true;
  try {
    const response = selectedEngineId.value
      ? await TalkviaCreditEnginesAPI.update(selectedEngineId.value, payload())
      : await TalkviaCreditEnginesAPI.create(payload());
    const saved = response.data;
    const index = engines.value.findIndex(engine => engine.id === saved.id);
    if (index >= 0) {
      engines.value.splice(index, 1, saved);
    } else {
      engines.value.unshift(saved);
    }
    assignForm(saved);
    useAlert(copy.saved);
  } catch (error) {
    useAlert(error?.response?.data?.error || copy.failedSave);
  } finally {
    isSaving.value = false;
  }
};

const removeEngine = async engine => {
  try {
    await TalkviaCreditEnginesAPI.delete(engine.id);
    engines.value = engines.value.filter(item => item.id !== engine.id);
    if (selectedEngineId.value === engine.id) resetForm();
    useAlert(copy.removed);
  } catch (error) {
    useAlert(error?.response?.data?.error || copy.failedRemove);
  }
};

const addStep = () => {
  const next = form.operational_steps.length + 1;
  form.operational_steps.push({
    id: `etapa-${next}`,
    title: `Nova etapa ${next}`,
    owner: '',
    sla_minutes: 15,
    description: '',
    checklist: [],
  });
};

const removeStep = index => {
  form.operational_steps.splice(index, 1);
};

const addStage = () => {
  const next = form.funnel_stages.length + 1;
  form.funnel_stages.push({
    id: `fase-${next}`,
    title: `Nova fase ${next}`,
    color: stageColors[(next - 1) % stageColors.length],
  });
};

const removeStage = index => {
  form.funnel_stages.splice(index, 1);
};

const toggleUf = (listName, uf) => {
  const list = form.eligibility_rules[listName] || [];
  if (list.includes(uf)) {
    form.eligibility_rules[listName] = list.filter(item => item !== uf);
  } else {
    form.eligibility_rules[listName] = [...list, uf];
    const oppositeListName =
      listName === 'allowed_ufs' ? 'blocked_ufs' : 'allowed_ufs';
    form.eligibility_rules[oppositeListName] = form.eligibility_rules[
      oppositeListName
    ].filter(item => item !== uf);
  }
};

const splitList = value => {
  if (Array.isArray(value)) return value;
  return String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
};

const numberOrNull = value => {
  if (value === '' || value === null || value === undefined) return null;
  return Number(value);
};

const formatDate = value => {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const stageToneClass = color =>
  ({
    cyan: 'border-cyan-400/70 bg-cyan-500/30 text-cyan-600 dark:text-cyan-200',
    indigo:
      'border-indigo-400/70 bg-indigo-500/30 text-indigo-700 dark:text-indigo-200',
    violet:
      'border-violet-400/70 bg-violet-500/30 text-violet-700 dark:text-violet-200',
    amber:
      'border-amber-400/70 bg-amber-500/30 text-amber-700 dark:text-amber-200',
    emerald:
      'border-emerald-400/70 bg-emerald-500/30 text-emerald-700 dark:text-emerald-200',
    rose: 'border-rose-400/70 bg-rose-500/30 text-rose-700 dark:text-rose-200',
  })[color] || 'border-n-weak bg-n-solid-2 text-n-slate-12';

const stageAccentClass = color =>
  ({
    cyan: 'bg-cyan-500',
    indigo: 'bg-indigo-500',
    violet: 'bg-violet-500',
    amber: 'bg-amber-500',
    emerald: 'bg-emerald-500',
    rose: 'bg-rose-500',
  })[color] || 'bg-n-slate-8';

const stageSwatchClass = color =>
  ({
    cyan: 'bg-cyan-500 ring-cyan-400/60',
    indigo: 'bg-indigo-500 ring-indigo-400/60',
    violet: 'bg-violet-500 ring-violet-400/60',
    amber: 'bg-amber-500 ring-amber-400/60',
    emerald: 'bg-emerald-500 ring-emerald-400/60',
    rose: 'bg-rose-500 ring-rose-400/60',
  })[color] || 'bg-n-slate-8 ring-n-slate-6';

onMounted(loadEngines);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main
    class="flex h-full min-h-0 flex-col overflow-auto bg-n-background p-6 text-n-slate-12"
  >
    <header
      class="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between"
    >
      <div class="max-w-3xl">
        <div
          class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-brand"
        >
          Crédito bancário
        </div>
        <h1 class="m-0 text-3xl font-semibold text-n-slate-12">
          {{ copy.title }}
        </h1>
        <p class="mb-0 mt-2 text-base leading-7 text-n-slate-11">
          {{ copy.description }}
        </p>
      </div>
      <div
        class="grid grid-cols-3 gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-3"
      >
        <div>
          <div class="text-2xl font-semibold">{{ engines.length }}</div>
          <div class="text-xs text-n-slate-10">Motores</div>
        </div>
        <div>
          <div class="text-2xl font-semibold">{{ activeCount }}</div>
          <div class="text-xs text-n-slate-10">Ativos</div>
        </div>
        <div>
          <div class="text-2xl font-semibold">{{ totalSla }}m</div>
          <div class="text-xs text-n-slate-10">SLA fluxo</div>
        </div>
      </div>
    </header>

    <section class="grid min-h-0 gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
      <aside
        class="flex min-h-0 flex-col rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div class="border-b border-n-weak p-4">
          <div class="mb-3 flex items-center justify-between gap-3">
            <h2 class="m-0 text-base font-semibold">Motores por banco</h2>
            <button
              class="inline-flex h-9 items-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white"
              @click="resetForm"
            >
              <i class="i-lucide-plus size-4" />
              {{ copy.newEngine }}
            </button>
          </div>
          <Input
            v-model="bankFilter"
            placeholder="Buscar banco, produto ou motor"
            type="search"
          />
        </div>

        <div v-if="isLoading" class="p-4 text-sm text-n-slate-11">
          {{ copy.loading }}
        </div>
        <div v-else-if="!engines.length" class="p-4 text-sm text-n-slate-11">
          {{ copy.empty }}
        </div>
        <div v-else class="min-h-0 flex-1 overflow-auto p-3">
          <section
            v-for="(items, bank) in groupedByBank"
            :key="bank"
            class="mb-4"
          >
            <h3
              class="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ bank }}
            </h3>
            <button
              v-for="engine in items"
              :key="engine.id"
              type="button"
              class="mb-2 w-full rounded-lg border p-3 text-left transition-colors"
              :class="
                selectedEngineId === engine.id
                  ? 'border-n-brand bg-n-brand/10'
                  : 'border-n-weak bg-n-solid-2 hover:border-n-slate-6'
              "
              @click="assignForm(engine)"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="truncate text-sm font-semibold">
                    {{ engine.name }}
                  </div>
                  <div class="mt-1 text-xs text-n-slate-10">
                    {{ engine.product_type || 'Produto não informado' }}
                  </div>
                </div>
                <span
                  class="rounded-full px-2 py-1 text-[11px] font-semibold"
                  :class="
                    engine.active
                      ? 'bg-emerald-500/10 text-emerald-600'
                      : 'bg-n-slate-5 text-n-slate-10'
                  "
                >
                  {{ engine.active ? 'Ativo' : 'Inativo' }}
                </span>
              </div>
              <p class="mb-0 mt-2 line-clamp-2 text-xs text-n-slate-11">
                {{ engine.eligibility_summary || 'Sem resumo de regra' }}
              </p>
            </button>
          </section>
        </div>
      </aside>

      <div class="grid min-w-0 gap-5 2xl:grid-cols-[minmax(0,1fr)_380px]">
        <form
          class="min-w-0 rounded-xl border border-n-weak bg-n-solid-1 p-5"
          @submit.prevent="saveEngine"
        >
          <div
            class="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
          >
            <div>
              <h2 class="m-0 text-xl font-semibold">
                {{ selectedEngine ? 'Editar motor' : 'Cadastrar motor' }}
              </h2>
              <p class="mb-0 mt-1 text-sm text-n-slate-11">
                Configure o roteiro operacional que será aplicado na consulta
                bancária.
              </p>
            </div>
            <div class="flex gap-2">
              <button
                v-if="selectedEngine"
                type="button"
                class="rounded-lg border border-n-weak px-4 py-2 text-sm"
                @click="resetForm"
              >
                {{ copy.cancel }}
              </button>
              <button
                type="submit"
                class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white"
                :disabled="isSaving || !formIsValid"
              >
                {{ selectedEngine ? copy.update : copy.save }}
              </button>
            </div>
          </div>

          <div
            v-if="formValidationMessages.length"
            class="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4"
          >
            <p
              class="mb-2 text-sm font-semibold text-amber-700 dark:text-amber-300"
            >
              {{ copy.invalidForm }}
            </p>
            <ul class="mb-0 list-disc space-y-1 pl-5 text-xs text-n-slate-11">
              <li v-for="message in formValidationMessages" :key="message">
                {{ message }}
              </li>
            </ul>
          </div>

          <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div class="relative space-y-1 text-sm font-medium">
              <div class="relative">
                <Input
                  v-model="form.bank_name"
                  label="Banco"
                  placeholder="Código ou nome do banco"
                  custom-input-class="!pr-24"
                  @focus="searchBanks"
                  @input="searchBanks"
                />
                <span
                  v-if="form.metadata.bank_code"
                  class="pointer-events-none absolute bottom-[9px] right-3 rounded-md bg-n-brand/10 px-2 py-1 text-xs font-semibold text-n-brand"
                >
                  {{ form.metadata.bank_code }}
                </span>
              </div>
              <div
                v-if="showBankOptions"
                class="absolute left-0 right-0 top-full z-20 mt-2 max-h-60 overflow-auto rounded-xl border border-n-weak bg-n-solid-2 p-2 shadow-xl"
              >
                <button
                  v-for="bank in bankOptions"
                  :key="bank.code"
                  type="button"
                  class="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-n-alpha-2"
                  @click="selectBank(bank)"
                >
                  <span class="font-medium text-n-slate-12">{{
                    bank.name
                  }}</span>
                  <span
                    class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-semibold text-n-slate-11"
                  >
                    {{ bank.code }}
                  </span>
                </button>
              </div>
              <p class="mb-0 text-xs text-n-slate-10">
                {{
                  isLoadingBanks
                    ? 'Buscando bancos...'
                    : 'Pesquise por código ou nome.'
                }}
              </p>
            </div>

            <Input
              v-model="form.name"
              label="Nome do motor"
              placeholder="INSS margem livre"
            />
            <Input
              v-model="form.code"
              label="Código interno"
              placeholder="auto ao salvar"
            />
            <Input v-model="form.priority" label="Prioridade" type="number" />

            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                Produto
              </label>
              <Select
                v-model="form.product_type"
                :options="productTypeOptions"
                class="!w-full"
              />
            </div>

            <Input
              v-model="form.agreement_type"
              label="Convênio"
              placeholder="INSS, SIAPE, FGTS..."
            />
            <Input
              v-model="form.metadata.table_name"
              label="Tabela/campanha"
              placeholder="Tabela vigente"
            />
            <label class="flex items-center gap-2 pt-7 text-sm font-medium">
              <input
                v-model="form.active"
                :class="checkboxClass"
                type="checkbox"
              />
              Motor ativo
            </label>
          </div>

          <div class="mt-6 rounded-xl border border-n-weak p-4">
            <h3 class="m-0 text-base font-semibold">
              Critérios de elegibilidade
            </h3>
            <div class="mt-4 grid gap-4 md:grid-cols-3 xl:grid-cols-6">
              <Input
                v-model="form.eligibility_rules.min_age"
                label="Idade mín."
                type="number"
              />
              <Input
                v-model="form.eligibility_rules.max_age"
                label="Idade máx."
                type="number"
              />
              <Input
                v-model="form.eligibility_rules.min_margin"
                label="Margem mín."
                type="number"
                step="0.01"
              />
              <Input
                v-model="form.eligibility_rules.max_installment"
                label="Parcela máx."
                type="number"
                step="0.01"
              />
              <Input
                v-model="form.eligibility_rules.min_income"
                label="Renda mín."
                type="number"
                step="0.01"
              />
              <Input
                v-model="form.eligibility_rules.max_term"
                label="Prazo máx."
                type="number"
              />
            </div>

            <div class="mt-4 grid gap-4 xl:grid-cols-2">
              <div>
                <div class="mb-2 text-sm font-medium">UFs permitidas</div>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="uf in ufOptions"
                    :key="uf"
                    type="button"
                    class="h-8 min-w-10 rounded-lg border px-2 text-xs font-semibold"
                    :class="
                      form.eligibility_rules.allowed_ufs.includes(uf)
                        ? 'border-n-brand bg-n-brand/10 text-n-brand'
                        : 'border-n-weak text-n-slate-11'
                    "
                    @click="toggleUf('allowed_ufs', uf)"
                  >
                    {{ uf }}
                  </button>
                </div>
              </div>
              <div>
                <div class="mb-2 text-sm font-medium">UFs bloqueadas</div>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="uf in ufOptions"
                    :key="uf"
                    type="button"
                    class="h-8 min-w-10 rounded-lg border px-2 text-xs font-semibold"
                    :class="
                      form.eligibility_rules.blocked_ufs.includes(uf)
                        ? 'border-red-500 bg-red-500/10 text-red-600'
                        : 'border-n-weak text-n-slate-11'
                    "
                    @click="toggleUf('blocked_ufs', uf)"
                  >
                    {{ uf }}
                  </button>
                </div>
              </div>
            </div>

            <div class="mt-4 grid gap-4 md:grid-cols-2">
              <Input
                v-model="form.eligibility_rules.allowed_benefit_types"
                label="Benefícios permitidos"
                placeholder="Aposentadoria, pensionista"
              />
              <Input
                v-model="form.eligibility_rules.allowed_agreements"
                label="Convênios permitidos"
                placeholder="INSS, SIAPE"
              />
              <label class="flex items-center gap-2 text-sm font-medium">
                <input
                  v-model="form.eligibility_rules.requires_authorization"
                  :class="checkboxClass"
                  type="checkbox"
                />
                Exige autorização antes da consulta
              </label>
              <Input
                v-model="form.eligibility_rules.notes"
                label="Observações do motor"
                placeholder="Restrições e exceções"
              />
            </div>
          </div>

          <div class="mt-6 rounded-xl border border-n-weak p-4">
            <div class="mb-4 flex items-center justify-between gap-3">
              <h3 class="m-0 text-base font-semibold">
                Roteiro operacional do banco
              </h3>
              <button
                type="button"
                class="rounded-lg border border-n-weak px-3 py-2 text-sm"
                @click="addStep"
              >
                Adicionar etapa
              </button>
            </div>
            <div class="space-y-3">
              <article
                v-for="(step, index) in form.operational_steps"
                :key="`${step.id}-${index}`"
                class="rounded-xl border border-n-weak bg-n-solid-2 p-4"
              >
                <div class="mb-3 flex items-center justify-between gap-3">
                  <div
                    class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-brand/10 text-sm font-semibold text-n-brand"
                  >
                    {{ index + 1 }}
                  </div>
                  <button
                    type="button"
                    class="text-sm text-red-500"
                    @click="removeStep(index)"
                  >
                    Remover
                  </button>
                </div>
                <div class="grid gap-3 md:grid-cols-[1fr_150px_130px]">
                  <Input v-model="step.title" placeholder="Nome da etapa" />
                  <Input v-model="step.owner" placeholder="Responsável" />
                  <Input
                    v-model="step.sla_minutes"
                    type="number"
                    placeholder="SLA min"
                  />
                </div>
                <TextArea
                  v-model="step.description"
                  class="mt-3"
                  placeholder="Descrição operacional"
                />
                <Input
                  v-model="step.checklist"
                  class="mt-3"
                  placeholder="Checklist separado por vírgula"
                />
              </article>
            </div>
          </div>
        </form>

        <aside class="min-w-0 space-y-5">
          <section class="rounded-xl border border-n-weak bg-n-solid-1 p-5">
            <h2 class="m-0 text-lg font-semibold">Funil visual do motor</h2>
            <p class="mb-4 mt-1 text-sm text-n-slate-11">
              Use estas fases para conectar a consulta bancária com o funil
              visual e medir onde a operação para.
            </p>
            <div class="space-y-3">
              <div
                v-for="(stage, index) in form.funnel_stages"
                :key="`${stage.id}-${index}`"
                class="relative overflow-hidden rounded-xl border bg-n-solid-2 p-4 shadow-sm transition-colors"
                :class="stageToneClass(stage.color)"
              >
                <span
                  class="absolute inset-y-0 left-0 w-1.5"
                  :class="stageAccentClass(stage.color)"
                />
                <div class="flex items-center gap-3 pl-2">
                  <div
                    class="flex size-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white shadow-sm"
                    :class="stageAccentClass(stage.color)"
                  >
                    {{ index + 1 }}
                  </div>
                  <input
                    v-model="stage.title"
                    class="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-semibold text-current outline-none"
                  />
                  <button
                    type="button"
                    class="rounded-lg px-2 py-1 text-xs font-medium text-current opacity-75 hover:bg-white/10 hover:opacity-100"
                    @click="removeStage(index)"
                  >
                    remover
                  </button>
                </div>
                <div class="mt-4 flex gap-2 pl-14">
                  <button
                    v-for="color in stageColors"
                    :key="color"
                    type="button"
                    class="size-6 rounded-full ring-2 ring-offset-2 ring-offset-n-solid-2 transition-transform hover:scale-110"
                    :class="[
                      stageSwatchClass(color),
                      stage.color === color ? 'scale-110' : 'opacity-80',
                    ]"
                    :title="color"
                    @click="stage.color = color"
                  />
                </div>
              </div>
            </div>
            <button
              type="button"
              class="mt-4 w-full rounded-lg border border-n-weak px-3 py-2 text-sm"
              @click="addStage"
            >
              Adicionar fase do funil
            </button>
          </section>

          <section class="rounded-xl border border-n-weak bg-n-solid-1 p-5">
            <h2 class="m-0 text-lg font-semibold">Resumo para consulta</h2>
            <dl class="mt-4 space-y-3 text-sm">
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-10">Banco</dt>
                <dd class="text-right font-medium">
                  {{ form.bank_name || '-' }}
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-10">Produto</dt>
                <dd class="text-right font-medium">
                  {{ form.product_type || '-' }}
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-10">Idade</dt>
                <dd class="text-right font-medium">
                  {{ form.eligibility_rules.min_age }} a
                  {{ form.eligibility_rules.max_age }}
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-10">UFs permitidas</dt>
                <dd class="text-right font-medium">
                  {{ form.eligibility_rules.allowed_ufs.join(', ') || 'Todas' }}
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-10">Atualizado</dt>
                <dd class="text-right font-medium">
                  {{
                    selectedEngine ? formatDate(selectedEngine.updated_at) : '-'
                  }}
                </dd>
              </div>
            </dl>
            <button
              v-if="selectedEngine"
              type="button"
              class="mt-5 w-full rounded-lg border border-red-500/40 px-3 py-2 text-sm font-medium text-red-500"
              @click="removeEngine(selectedEngine)"
            >
              Remover motor
            </button>
          </section>
        </aside>
      </div>
    </section>
  </main>
</template>
